import React, { useState } from "react";
import { Link, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { Lock, Mail, User, Phone, LogOut, Sparkles, AlertCircle } from "lucide-react";
import { Input } from "../components/ui/input";
import { PortalAuthProvider, usePortal } from "../lib/portalAuth";
import { toast } from "sonner";
import { trackPortalSignup } from "../lib/tracking";

function apiErrMsg(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

function Shell({ children }) {
  return (
    <div className="min-h-[calc(100vh-240px)] bg-[color:var(--alpha-bg-soft)] py-16">
      <div className="alpha-container max-w-5xl">{children}</div>
    </div>
  );
}

function LoginView() {
  const { login } = usePortal();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(form.email, form.password);
      toast.success("Signed in");
      nav("/portal");
    } catch (e) {
      setErr(apiErrMsg(e?.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell>
      <div className="grid md:grid-cols-2 gap-8 items-center">
        <div>
          <div className="alpha-overline">Participant portal</div>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-medium tracking-tight">Welcome back.</h1>
          <p className="mt-4 text-[color:var(--alpha-mute)]">Sign in to view your plan overview, supports and referrals with Alpha Community Care.</p>
          <div className="mt-6 text-sm text-[color:var(--alpha-mute)]">
            New to Alpha? <Link to="/portal/register" className="text-[color:var(--alpha-purple-700)] font-semibold">Create an account</Link>
          </div>
        </div>
        <form onSubmit={submit} className="alpha-card p-8 space-y-4" data-testid="portal-login-form">
          <div>
            <label className="text-sm font-semibold flex items-center gap-2"><Mail className="w-4 h-4" /> Email</label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="portal-login-email" className="mt-2 h-11" />
          </div>
          <div>
            <label className="text-sm font-semibold flex items-center gap-2"><Lock className="w-4 h-4" /> Password</label>
            <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="portal-login-password" className="mt-2 h-11" />
          </div>
          {err && <div className="text-sm text-red-600 flex items-center gap-1" data-testid="portal-login-error"><AlertCircle className="w-4 h-4" /> {err}</div>}
          <button disabled={loading} className="alpha-btn-primary w-full justify-center" data-testid="portal-login-submit">
            {loading ? "Signing in…" : "Sign in"}
          </button>
          <p className="text-xs text-[color:var(--alpha-mute)] text-center">
            By continuing you agree to our <Link to="/privacy" className="underline">Privacy Policy</Link>.
          </p>
        </form>
      </div>
    </Shell>
  );
}

function RegisterView() {
  const { register } = usePortal();
  const nav = useNavigate();
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await register(form);
      trackPortalSignup({ email: form.email });
      toast.success("Account created");
      nav("/portal");
    } catch (e) {
      setErr(apiErrMsg(e?.response?.data?.detail) || "Sign-up failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell>
      <div className="grid md:grid-cols-2 gap-8 items-center">
        <div>
          <div className="alpha-overline">Participant portal</div>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-medium tracking-tight">Create your account.</h1>
          <p className="mt-4 text-[color:var(--alpha-mute)]">This is a scaffold of our participant portal. Over time, you'll be able to view your supports, upload documents and message your coordinator here.</p>
          <div className="mt-6 text-sm text-[color:var(--alpha-mute)]">
            Already have an account? <Link to="/portal/login" className="text-[color:var(--alpha-purple-700)] font-semibold">Sign in</Link>
          </div>
        </div>
        <form onSubmit={submit} className="alpha-card p-8 space-y-4" data-testid="portal-register-form">
          <div>
            <label className="text-sm font-semibold flex items-center gap-2"><User className="w-4 h-4" /> Full name</label>
            <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required data-testid="portal-reg-name" className="mt-2 h-11" />
          </div>
          <div>
            <label className="text-sm font-semibold flex items-center gap-2"><Mail className="w-4 h-4" /> Email</label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="portal-reg-email" className="mt-2 h-11" />
          </div>
          <div>
            <label className="text-sm font-semibold flex items-center gap-2"><Phone className="w-4 h-4" /> Phone (optional)</label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="portal-reg-phone" className="mt-2 h-11" />
          </div>
          <div>
            <label className="text-sm font-semibold flex items-center gap-2"><Lock className="w-4 h-4" /> Password</label>
            <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} minLength={8} required data-testid="portal-reg-password" className="mt-2 h-11" />
            <div className="text-[11px] text-[color:var(--alpha-mute)] mt-1">At least 8 characters.</div>
          </div>
          {err && <div className="text-sm text-red-600 flex items-center gap-1" data-testid="portal-reg-error"><AlertCircle className="w-4 h-4" /> {err}</div>}
          <button disabled={loading} className="alpha-btn-primary w-full justify-center" data-testid="portal-reg-submit">
            {loading ? "Creating account…" : "Create account"}
          </button>
        </form>
      </div>
    </Shell>
  );
}

function DashboardView() {
  const { user, logout, portalApi } = usePortal();
  const nav = useNavigate();
  const [plan, setPlan] = useState(null);

  React.useEffect(() => {
    portalApi.get("/portal/my-plan").then((r) => setPlan(r.data)).catch(() => {});
  }, [portalApi]);

  return (
    <Shell>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="alpha-overline">Participant portal</div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-medium">Welcome, {user?.full_name?.split(" ")[0] || "there"}.</h1>
        </div>
        <button onClick={() => { logout(); nav("/"); }} className="alpha-btn-ghost text-sm" data-testid="portal-logout">
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </div>

      <div className="mt-10 grid md:grid-cols-3 gap-5">
        <div className="alpha-card p-6 md:col-span-2">
          <div className="alpha-overline">Your NDIS plan</div>
          <h3 className="mt-2 font-display text-xl font-semibold">Plan overview</h3>
          <div className="mt-4 p-4 rounded-xl bg-[color:var(--alpha-purple-50)] border border-[color:var(--alpha-purple-200)] text-sm" data-testid="portal-plan-stub">
            <div className="flex items-center gap-2 text-[color:var(--alpha-purple-700)] font-semibold">
              <Sparkles className="w-4 h-4" /> Scaffold
            </div>
            <p className="mt-2 text-[color:var(--alpha-ink)]">
              This area will show your supports, goals and recent activity once your coordinator has connected your plan. For now, contact us directly for plan assistance.
            </p>
          </div>
          {plan?.plan?.message && (
            <p className="mt-4 text-sm text-[color:var(--alpha-mute)]">{plan.plan.message}</p>
          )}
        </div>

        <div className="alpha-card p-6">
          <div className="alpha-overline">Your details</div>
          <ul className="mt-3 space-y-2 text-sm">
            <li><span className="text-[color:var(--alpha-mute)]">Email:</span> <div className="font-semibold">{user?.email}</div></li>
            {user?.phone && <li><span className="text-[color:var(--alpha-mute)]">Phone:</span> <div className="font-semibold">{user.phone}</div></li>}
            <li><span className="text-[color:var(--alpha-mute)]">Member since:</span> <div className="font-semibold">{user?.created_at?.slice(0, 10)}</div></li>
          </ul>
        </div>
      </div>

      <div className="mt-10 grid md:grid-cols-3 gap-5">
        <Link to="/referrals" className="alpha-card p-6 hover:bg-[color:var(--alpha-purple-50)]">
          <h3 className="font-display text-lg font-semibold">Refer someone</h3>
          <p className="text-sm text-[color:var(--alpha-mute)] mt-1">Refer a family member, friend or participant.</p>
        </Link>
        <Link to="/vacancies" className="alpha-card p-6 hover:bg-[color:var(--alpha-purple-50)]">
          <h3 className="font-display text-lg font-semibold">Browse vacancies</h3>
          <p className="text-sm text-[color:var(--alpha-mute)] mt-1">Explore available homes across Australia.</p>
        </Link>
        <Link to="/contact" className="alpha-card p-6 hover:bg-[color:var(--alpha-purple-50)]">
          <h3 className="font-display text-lg font-semibold">Contact intake</h3>
          <p className="text-sm text-[color:var(--alpha-mute)] mt-1">Speak to our team about your NDIS supports.</p>
        </Link>
      </div>
    </Shell>
  );
}

function ProtectedDashboard() {
  const { user, loading } = usePortal();
  if (loading) return <Shell><div className="text-center py-12 text-[color:var(--alpha-mute)]">Loading…</div></Shell>;
  if (!user) return <Navigate to="/portal/login" replace />;
  return <DashboardView />;
}

export default function PortalRoutes() {
  return (
    <PortalAuthProvider>
      <div data-testid="portal-root">
        <Routes>
          <Route index element={<ProtectedDashboard />} />
          <Route path="login" element={<LoginView />} />
          <Route path="register" element={<RegisterView />} />
          <Route path="my-plan" element={<ProtectedDashboard />} />
          <Route path="*" element={<Navigate to="/portal" replace />} />
        </Routes>
      </div>
    </PortalAuthProvider>
  );
}
